import React from 'react'
import FoldableComponent from '../FoldableComponent'
import Slider from 'rc-slider'
import { useSelector } from 'react-redux'

interface QuestionAnswerListProps {
	questions: Question[]
	answers: any[]
	initialOpen?: boolean
}

export interface Question {
	type: string
	id: string
	attributes: Attributes
}

export interface Attributes {
	parent_id: string
	type: string
	title: string
	context: string
	listPosition: number
	options?: Options
	created_at: string
	updated_at: string
}

export interface Options {
	items?: any[]
	excludeRoles?: string[]
	ignore: boolean
	max_words: number
	min?: number
	max?: number
	minLabel?: string
	maxLabel?: string
}

function QuestionAnswerList({
	questions,
	answers,
	initialOpen = false,
}: QuestionAnswerListProps) {
	const { user } = useSelector((state) => state['auth'])
	const userRole: string = user.attributes.role

	return (
		<>
			{questions
				?.filter((a) => {
					//Filter question out if matches user's role
					return !a.attributes.options?.excludeRoles?.includes(userRole)
				})
				.sort((a, b) => a.attributes.listPosition - b.attributes.listPosition)
				.map((q) => {
					const answerObject = answers.find(
						(a) => a.attributes.questionId === q.id
					)

					const answer: string | string[] | number = (() => {
						switch (q.attributes.type) {
							case 'text':
							case 'scale':
								return answerObject?.attributes?.answer
							case 'select':
								return q.attributes.options?.items?.find(
									(a) => a.id === answerObject?.attributes?.answer
								)?.label
							case 'select.multi':
							case 'ranking':
								return answerObject?.attributes?.answer.map(
									(each) =>
										q.attributes.options.items.find((a) => a.id === each).label
								)
							default:
								return 'No answer given'
						}
					})()

					return (
						<FoldableComponent
							initialOpen={initialOpen}
							key={q.id}
							title={q.attributes.title}
						>
							<div className="p-4 bg-gray-50">
								{['string', 'undefined'].includes(typeof answer) && (
									<p
										className={`whitespace-pre-wrap ${
											answer ? 'text-black' : 'text-gray-400'
										}`}
									>
										{answer}
									</p>
								)}
								{answer && q.attributes.type === 'scale' && (
									<div className="px-12 pb-10">
										<Slider
											min={q.attributes.options.min || 0}
											marks={{
												[q.attributes.options.min || 0]: {
													label:
														q.attributes.options.minLabel ||
														'Strongly disagree',
												},
												[q.attributes.options.max || 5]: {
													label:
														q.attributes.options.maxLabel || 'Strongly agree',
												},
											}}
											dotStyle={{
												width: 13,
												height: 13,
												bottom: -4,
												marginLeft: -6.5,
											}}
											included={false}
											defaultValue={null}
											value={answer as number}
											disabled={true}
											dots
											max={q.attributes.options.max || 5}
											handleStyle={{
												backgroundColor: '#6435D1',
												borderColor: '#6435D1',
												width: '18px',
												height: '18px',
												marginTop: '-8px',
											}}
										/>
									</div>
								)}
								{!answer && <p>No answer</p>}
								{Array.isArray(answer) && (
									<ul
										className={`${
											q.attributes.type === 'ranking'
												? 'list-decimal'
												: 'list-disc'
										} ml-4`}
									>
										{answer.map((a) => (
											<li key={'Answer' + a}>{a}</li>
										))}
									</ul>
								)}
							</div>
						</FoldableComponent>
					)
				})}
		</>
	)
}

export default QuestionAnswerList
